/**
 * /chatbot/proposal?session_token=<token>
 *
 * Standalone proposal page presented to qualified chatbot prospects.
 * Shows what they're getting, the onboarding process, T&C clickwrap,
 * and a CTA that redirects to Stripe Checkout.
 *
 * Related: agent-ops #57 (E10)
 */

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { knkCheckoutFields } from '../lib/knkTracking';
import {
  Cpu,
  BookOpen,
  Monitor,
  CheckCircle2,
  Search,
  Settings,
  GraduationCap,
  Rocket,
  HeadphonesIcon,
  Loader2,
  AlertCircle,
} from 'lucide-react';

// ── Proposal content ──────────────────────────────────────────

const WHAT_YOU_GET = [
  {
    icon: Settings,
    color: '#7B61FF',
    title: 'The Setup',
    points: [
      '3-hour onboarding session',
      'OpenClaw configured to your processes',
      'Vito deployed and live on your site',
    ],
  },
  {
    icon: BookOpen,
    color: '#10B981',
    title: 'The Training',
    points: [
      'Your SOPs encoded into the system',
      'Your team trained and ready',
      'Your workflows automated end-to-end',
    ],
  },
  {
    icon: Monitor,
    color: '#3B82F6',
    title: 'The Machine',
    points: [
      'Mac Mini shipped to your desk',
      'Pre-configured out of the box',
      'Plug in, power on — done',
    ],
  },
];

const PROCESS_STEPS = [
  { icon: Search, label: 'Discovery', description: 'We learn your business, your team, your goals.' },
  { icon: Settings, label: 'Configuration', description: 'OpenClaw is wired to your exact processes.' },
  { icon: GraduationCap, label: 'Training', description: 'Your SOPs go in. Your team gets hands-on time.' },
  { icon: Rocket, label: 'Deployment', description: 'Vito goes live. Hardware ships to your door.' },
  { icon: HeadphonesIcon, label: 'Support', description: 'Ongoing access to the team. We don\'t disappear.' },
];

// ── Page component ────────────────────────────────────────────

export default function ChatbotProposalPage() {
  const [searchParams] = useSearchParams();
  const sessionToken = searchParams.get('session_token');

  const [tcChecked1, setTcChecked1] = useState(false);
  const [tcChecked2, setTcChecked2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bothChecked = tcChecked1 && tcChecked2;

  // No session token — show expired state
  if (!sessionToken) {
    return (
      <main className="min-h-screen bg-[#0A0A14] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-12 h-12 rounded-2xl bg-[#7B61FF]/10 border border-[#7B61FF]/20 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-6 h-6 text-[#7B61FF]" />
          </div>
          <h1 className="text-2xl font-heading font-semibold text-[#F0EFF4] mb-3">
            This link has expired
          </h1>
          <p className="text-[#F0EFF4]/60 font-heading mb-8">
            Chat with Vito to get a fresh proposal link.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-heading font-semibold transition-colors"
          >
            Back to RoofClaw
          </a>
        </div>
      </main>
    );
  }

  async function handleCheckout() {
    if (!bothChecked || loading) return;
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/chat/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_token: sessionToken, ...knkCheckoutFields() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        setLoading(false);
        return;
      }

      // Redirect to Stripe Checkout
      window.location.href = data.checkout_url;
    } catch {
      setError('Network error. Check your connection and try again.');
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] font-heading selection:bg-[#7B61FF] selection:text-white">

      {/* Header */}
      <header className="px-6 py-5 border-b border-[#F0EFF4]/10 flex items-center justify-between max-w-5xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <span className="text-xl font-heading font-bold tracking-tight text-[#F0EFF4]">
            RoofClaw
          </span>
          <span className="hidden sm:inline-flex items-center px-2.5 py-0.5 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-data font-semibold tracking-widest text-[#7B61FF]">
            PROPOSAL
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Cpu className="w-4 h-4 text-[#7B61FF]" />
          <span className="text-sm font-data text-[#F0EFF4]/40 tracking-wide">SECURE</span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-24">

        {/* Hero */}
        <section className="pt-16 pb-12 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-data font-semibold tracking-widest text-[#7B61FF] mb-6">
            <CheckCircle2 className="w-3.5 h-3.5" />
            QUALIFIED PROSPECT
          </div>
          <h1 className="text-4xl sm:text-5xl font-heading font-bold text-[#F0EFF4] mb-5 leading-tight">
            Your RoofClaw<br />
            <span className="text-[#7B61FF]">Proposal</span>
          </h1>
          <p className="text-lg text-[#F0EFF4]/60 max-w-xl mx-auto leading-relaxed">
            You passed qualification. Here's exactly what you're getting,
            how it works, and how to claim your spot.
          </p>
        </section>

        {/* Section 1: What you're getting */}
        <section className="mb-16">
          <h2 className="text-sm font-data font-semibold tracking-widest text-[#F0EFF4]/40 uppercase mb-8 text-center">
            What You're Getting
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {WHAT_YOU_GET.map(({ icon: Icon, color, title, points }) => (
              <div
                key={title}
                className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-6"
              >
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${color}18` }}
                >
                  <Icon size={18} style={{ color }} />
                </div>
                <h3 className="font-heading font-semibold text-[#F0EFF4] mb-3">{title}</h3>
                <ul className="space-y-2">
                  {points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2 text-sm text-[#F0EFF4]/60">
                      <CheckCircle2
                        className="w-4 h-4 mt-0.5 shrink-0"
                        style={{ color }}
                      />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: Process */}
        <section className="mb-16">
          <h2 className="text-sm font-data font-semibold tracking-widest text-[#F0EFF4]/40 uppercase mb-8 text-center">
            How It Works
          </h2>
          <div className="relative">
            {/* connector line — hidden on mobile */}
            <div className="hidden md:block absolute top-8 left-[calc(10%+20px)] right-[calc(10%+20px)] h-px bg-[#F0EFF4]/10" />
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
              {PROCESS_STEPS.map(({ icon: Icon, label, description }, i) => (
                <div key={label} className="flex flex-col items-center text-center relative">
                  <div className="w-16 h-16 rounded-2xl bg-[#18181B] border border-[#F0EFF4]/10 flex items-center justify-center mb-3 relative z-10">
                    <Icon className="w-6 h-6 text-[#7B61FF]" />
                  </div>
                  <span className="text-xs font-data font-semibold tracking-wider text-[#7B61FF] mb-1">
                    0{i + 1}
                  </span>
                  <span className="font-heading font-semibold text-[#F0EFF4] text-sm mb-1">
                    {label}
                  </span>
                  <p className="text-xs text-[#F0EFF4]/50 leading-relaxed">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 3: T&C Clickwrap */}
        <section className="mb-10">
          <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-6 max-w-2xl mx-auto">
            <h2 className="font-heading font-semibold text-[#F0EFF4] mb-5 text-center">
              Before You Claim Your Spot
            </h2>
            <div className="space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={tcChecked1}
                    onChange={(e) => setTcChecked1(e.target.checked)}
                  />
                  <div className="w-5 h-5 rounded border border-[#F0EFF4]/20 bg-[#0A0A14] peer-checked:bg-[#7B61FF] peer-checked:border-[#7B61FF] transition-colors flex items-center justify-center">
                    {tcChecked1 && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-[#F0EFF4]/70 leading-relaxed group-hover:text-[#F0EFF4]/90 transition-colors">
                  I understand RoofClaw is a professional service, not a plug-and-play product.
                  Success requires my team's participation in the 3-hour onboarding session.
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group">
                <div className="relative mt-0.5 shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={tcChecked2}
                    onChange={(e) => setTcChecked2(e.target.checked)}
                  />
                  <div className="w-5 h-5 rounded border border-[#F0EFF4]/20 bg-[#0A0A14] peer-checked:bg-[#7B61FF] peer-checked:border-[#7B61FF] transition-colors flex items-center justify-center">
                    {tcChecked2 && (
                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
                        <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-sm text-[#F0EFF4]/70 leading-relaxed group-hover:text-[#F0EFF4]/90 transition-colors">
                  I confirm I am the decision-maker at a residential roofing company and authorized
                  to make this purchase.
                </span>
              </label>
            </div>
            <p className="text-xs text-[#F0EFF4]/40 text-center mt-4">
              By proceeding, you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:underline">
                Terms &amp; Conditions
              </a>.
            </p>
          </div>
        </section>

        {/* Section 4: CTA */}
        <section className="text-center">
          {error && (
            <div className="flex items-center gap-2 justify-center text-red-400 text-sm mb-4">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <button
            onClick={handleCheckout}
            disabled={!bothChecked || loading}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-heading font-semibold text-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed bg-[#7B61FF] hover:bg-[#6B51EF] text-white disabled:hover:bg-[#7B61FF] shadow-lg shadow-[#7B61FF]/20"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Taking you to checkout…
              </>
            ) : (
              <>
                <Rocket className="w-5 h-5" />
                Claim Your RoofClaw — $10,000 USD
              </>
            )}
          </button>

          <p className="mt-4 text-xs text-[#F0EFF4]/30 font-data">
            Checkout total displayed in CAD. Free shipping included. State/local duties may apply at delivery.
          </p>
        </section>

      </main>
    </div>
  );
}
